/**
 * SortableColumnHeader Component
 * 
 * A clickable column header that displays sort indicators (arrows)
 * and handles sort state cycling: Ascending -> Descending -> Reset
 * 
 * Per spec: Arrow icon (↑ or ↓) appears next to the text when active. 
 * It is hidden when reset/not sorted.
 */

import React from 'react';
import styled, { keyframes } from 'styled-components';
import { CaretUpOutlined, CaretDownOutlined, LoadingOutlined } from '@ant-design/icons';
import { SortState } from '../../hooks/useTableSort';

// ========== TYPE DEFINITIONS ==========

export interface SortableColumnHeaderProps {
  /** Column title to display */
  title: string;
  /** Field name used for sorting (matches backend field name) */
  field: string;
  /** Current sort state from useTableSort hook */
  sortState: SortState;
  /** Callback when header is clicked */
  onSort: (field: string) => void;
  /** Whether sorting is disabled for this column */
  disabled?: boolean;
  /** Whether sorting is currently loading (show spinner on active column) */
  isLoading?: boolean;
  /** Custom className for additional styling */
  className?: string;
}

// ========== STYLED COMPONENTS ==========

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const HeaderWrapper = styled.div<{ $disabled?: boolean; $clickable?: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: ${props => props.$disabled ? 'not-allowed' : props.$clickable ? 'pointer' : 'default'};
  user-select: none;
  transition: all 0.2s ease;
  opacity: ${props => props.$disabled ? 0.6 : 1};
  width: 100%;
  height: 100%;
  padding: 8px 8px;
  margin: 0px;
  border-radius: 4px;

  &:hover {
    color: ${props => (props.$disabled || !props.$clickable) ? 'inherit' : '#2563EB'};
    background-color: ${props => (props.$disabled || !props.$clickable) ? 'transparent' : 'rgba(37, 99, 235, 0.08)'};
  }

  &:active {
    background-color: ${props => (props.$disabled || !props.$clickable) ? 'transparent' : 'rgba(37, 99, 235, 0.15)'};
  }
`;

const HeaderText = styled.span`
  white-space: nowrap;
`;

const SortIconWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: 4px;
  width: 14px;
  height: 14px;
`;

const ArrowIcon = styled.span<{ $direction: 'ascending' | 'descending' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  color: #2563EB;
  font-size: 12px;
  line-height: 1;

  .anticon {
    font-size: 12px;
  }
`;

const LoadingIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  color: #2563EB;
  font-size: 12px;
  line-height: 1;
  animation: ${spin} 1s linear infinite;

  .anticon {
    font-size: 12px;
  }
`;

// ========== MAIN COMPONENT ==========

/**
 * Sortable column header component
 * 
 * Per spec: Visual arrow icon (↑ or ↓) appears next to the text when active.
 * It is hidden when reset/not sorted. Only one column can be sorted at a time.
 * 
 * @example
 * // Basic usage
 * <SortableColumnHeader
 *   title="Status"
 *   field="Status"
 *   sortState={sortState}
 *   onSort={handleSort}
 *   isLoading={isSortLoading}
 *   disabled={isSortLoading}
 * />
 */
const SortableColumnHeader: React.FC<SortableColumnHeaderProps> = ({
  title,
  field,
  sortState,
  onSort,
  disabled = false,
  isLoading = false,
  className,
}) => {
  const isActive = sortState.field === field && sortState.direction !== null;
  const direction = sortState.field === field ? sortState.direction : null;
  const showLoading = isLoading && isActive;

  const handleClick = () => {
    if (!disabled) {
      onSort(field);
    }
  };

  return (
    <HeaderWrapper 
      onClick={handleClick} 
      $disabled={disabled}
      $clickable={!disabled}
      className={className}
    >
      <HeaderText>{title}</HeaderText>
      {/* Only show sort indicator when this column is actively sorted */}
      {isActive && direction && (
        <SortIconWrapper>
          {showLoading ? (
            <LoadingIcon>
              <LoadingOutlined />
            </LoadingIcon>
          ) : (
            <ArrowIcon $direction={direction}>
              {direction === 'ascending' ? <CaretUpOutlined /> : <CaretDownOutlined />}
            </ArrowIcon>
          )}
        </SortIconWrapper>
      )}
    </HeaderWrapper>
  );
};

export default SortableColumnHeader;
